import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import { DayMenu } from '../meal/DayMenu';
import { useNavigate } from 'react-router-dom';
import SignUpSection from '../user/SignupSection';
import { onboardUser } from '../../state/user/userSlice';
import { useTranslation } from 'react-i18next';
import { BodyMeasurements } from '../../models/BodyMeasurements';
import { useNotifications } from '../common/notification/NotificationContext';

export function OnboardingClientDashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { addNotification } = useNotifications()

  const recommendedMeals = useSelector((state: RootState) => state.meal.recommendedMeals);
  const newUser = useSelector((state: RootState) => state.user.newUser);
  const user = useSelector((state: RootState) => state.user.user);

  const dailyNutrients = useSelector((state: RootState) => state.user.newUser.dailyNutrients) || {};

  useEffect(() => {
    if (user && user.id) {
      navigate(`/user/${user.id}/home`);
    }
  }, [user, navigate]);

  const handleEmailSubmit = async () => {
    try {
      await dispatch(onboardUser({
        user: newUser,
        meals: recommendedMeals.map((item) => item.meal)
      })).unwrap()
    }
    catch (error) {
      addNotification(String(error), "warning")
    }
  }

  const resolveBMI = (type: BodyMeasurements.type): number => {
    return newUser.bodyMeasurements?.find(bm => bm.type === type)?.bmi || 0.0;
  };

  const resolveWeight = (type: BodyMeasurements.type): number => {
    return newUser.bodyMeasurements?.find(bm => bm.type === type)?.weight || 0.0;
  };

  const formatWeight = (value: number): string => {
    return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
  };

  const handlePrimaryCta = () => {
    document.getElementById('onboarding-recommended')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <Box
      sx={{
        p: 3,
        fontFamily: 'sans-serif',
        backgroundColor: '#FBF7F2',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          mb: 4,
          p: 2,
          textAlign: 'center',
          backgroundColor: '#F3EEE8'
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {t('onboarding_dashboard.header')}
        </Typography>
        <Typography variant="body1">
          {t('onboarding_dashboard.subheader')}
        </Typography>
        <Button
          variant="contained"
          onClick={handlePrimaryCta}
          sx={{
            mt: 2,
            backgroundColor: '#7A4A2E',
            color: '#FBF7F2',
            '&:hover': { backgroundColor: '#5F3923' },
            textTransform: 'none',
            fontWeight: 700
          }}
        >
          {t('onboarding_dashboard.primary_cta')}
        </Button>
      </Box>
      <Box
        id="onboarding-recommended"
        sx={{
          border: '1px solid #9C948C',
          borderRadius: 2,
          mb: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          boxShadow: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('onboarding_dashboard.health_overview.label')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 3,
            mb: 2,
          }}
        >
          <Typography variant="body1">
            <strong>{t('onboarding_dashboard.health_overview.bmi')}:</strong> {resolveBMI(BodyMeasurements.type.ACTUAL).toFixed(1)}
          </Typography>
          <Typography variant="body1">
            <strong>{t('onboarding_dashboard.health_overview.goal')}:</strong> {resolveBMI(BodyMeasurements.type.PLAN).toFixed(1)} {t('onboarding_dashboard.health_overview.bmi')}
          </Typography>
          <Typography variant="body1">
            <strong>{t('onboarding_dashboard.health_overview.daily_calories')}:</strong>{' '}
            {dailyNutrients['ENERGY']?.toFixed(0) || 0} kcal
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 3,
            mb: 2,
          }}
        >
          <Typography variant="body1">
            <strong>{t('onboarding_dashboard.health_overview.weight')}:</strong> {formatWeight(resolveWeight(BodyMeasurements.type.ACTUAL))} kg
          </Typography>
          <Typography variant="body1">
            <strong>{t('onboarding_dashboard.health_overview.goal')}:</strong> {formatWeight(resolveWeight(BodyMeasurements.type.PLAN))} kg
          </Typography>
        </Box>
        <Box mt={2} mb={2}>
          <Typography variant="body2" fontStyle="italic" fontWeight={600}>
            {t('onboarding_dashboard.motivational_message')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid #9C948C',
          borderRadius: 2,
          mb: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          boxShadow: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('onboarding_dashboard.nutritional_goals')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {Object.entries(dailyNutrients).map(([key, value]) => {
            const displayValue =
              key === 'ENERGY'
                ? `${value.toFixed(0)} kcal`
                : `${value.toFixed(1)} g`;
            return (
              <Box
                key={key}
                sx={{
                  border: '1px solid #9C948C',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#F3EEE8',
                  minWidth: '120px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {t(`common.nutrient.${key}`)}
                </Typography>
                <Typography variant="body2">{displayValue}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          border: '1px solid #9C948C',
          borderRadius: 2,
          mb: 4,
          p: 3,
          backgroundColor: '#FFFFFF',
          boxShadow: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('onboarding_dashboard.recommended_meals.label')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('onboarding_dashboard.recommended_meals.header')}
        </Typography>
        <DayMenu meals={recommendedMeals.map((item) => item.meal)} />
      </Box>

      <Box
        sx={{
          border: '1px solid #D8CEC4',
          borderRadius: 2,
          p: 3,
          backgroundColor: '#F8F4EF',
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('signup_section.save_my_progress')}
        </Typography>
        <Typography variant="body2">
          {t('onboarding_dashboard.cta_message')}
        </Typography>
        <SignUpSection onSubmit={handleEmailSubmit} />
      </Box>
    </Box>
  );
}
